import {
  Scope,
  DateRange,
  // NestedCommission,
  SignedInUser,
  // TODO,
} from "@revelate/types";
import { create } from "zustand";
import { devtools, persist } from "zustand/middleware";
import type {} from "@redux-devtools/extension"; // required for devtools typing
import { getUser } from "@/lib/supabase";
import { defaultDateRange, defaultDateRangeValue } from "@/constants/dateRange";
import { parseISO, format } from "date-fns";
import { OnChangeFn, VisibilityState } from "@tanstack/react-table";
import { Session } from "@supabase/supabase-js";
import { getUserRoleFromSession } from "@/lib/app/user_roles";
import { Database } from "@revelate/types/supabase";

// Define loading state types
export type LoadingState = {
  isSyncing: boolean;
  isCalculating: boolean;
};

interface AppState {
  scope: Scope | null;
  setScope: (scope: Scope | null) => void;
  columnVisibility: VisibilityState;
  setColumnVisibility: OnChangeFn<VisibilityState>;
  dateRangeValue: string;
  setDateRangeValue: (dateValue: string) => void;
  dateRange: DateRange;
  setDateRange: (dateRange?: DateRange) => void;
  currentUser: SignedInUser | null;
  companyCurrency: Database["public"]["Tables"]["currencies"]["Row"] | null;
  setCompanyCurrency: (companyCurrency: Database["public"]["Tables"]["currencies"]["Row"] | null) => void;
  loadingState: LoadingState;
  setLoadingState: (state: Partial<LoadingState>) => void;
  isCalculating: boolean;
  isSyncing: boolean;
  setCalculating: (isCalculating: boolean) => void;
  setSyncing: (isSyncing: boolean) => void;
  fetch: (session: Session | null) => Promise<SignedInUser | null>;
  reset: () => void;
  isLoaded: () => boolean;
}

const defaultColumnVisibility = {
  provider: false,
  provider_id: false,
  is_closed: false,
  is_invoiced: false,
  is_paid: false,
  days_to_close: false,
  created_at: false,
};

const initialState: AppState = {
  scope: null,
  setScope: () => {},
  columnVisibility: defaultColumnVisibility,
  setColumnVisibility: () => defaultColumnVisibility,
  dateRangeValue: defaultDateRangeValue,
  setDateRangeValue: () => {},
  dateRange: defaultDateRange,
  setDateRange: () => {},
  currentUser: null,
  companyCurrency: null,
  setCompanyCurrency: () => {},
  loadingState: { isSyncing: false, isCalculating: false },
  setLoadingState: () => {},
  isCalculating: false,
  isSyncing: false,
  setCalculating: () => {},
  setSyncing: () => {},
  fetch: () => Promise.resolve(null),
  reset: () => {},
  isLoaded: () => false,
};

const updateURLParams = (dateRange?: DateRange) => {
  const url = new URL(window.location.href);
  if (dateRange?.from) {
    url.searchParams.set("from", format(dateRange.from, "yyyy-MM-dd"));
  } else {
    url.searchParams.delete("from");
  }
  if (dateRange?.to) {
    url.searchParams.set("to", format(dateRange.to, "yyyy-MM-dd"));
  } else {
    url.searchParams.delete("to");
  }
  window.history.replaceState({}, "", url.toString());
};

const getDateRangeFromURL = (): DateRange => {
  const url = new URL(window.location.href);
  const from = url.searchParams.get("from");
  const to = url.searchParams.get("to");
  return {
    from: from ? parseISO(from) : undefined,
    to: to ? parseISO(to) : undefined,
  };
};

export const useAppStore = create<AppState>()(
  devtools(
    persist(
      (set, get) => ({
        ...initialState,
        loadingState: { isSyncing: false, isCalculating: false },
        isCalculating: false,
        isSyncing: false,
        
        setLoadingState: (newState) => {
          set((state) => ({
            loadingState: { ...state.loadingState, ...newState },
            isCalculating: newState.isCalculating !== undefined ? newState.isCalculating : state.loadingState.isCalculating,
            isSyncing: newState.isSyncing !== undefined ? newState.isSyncing : state.loadingState.isSyncing,
          }));
        },
        
        setCalculating: (isCalculating) => {
          set((state) => ({
            loadingState: { ...state.loadingState, isCalculating },
            isCalculating,
          }));
        },
        
        setSyncing: (isSyncing) => {
          set((state) => ({
            loadingState: { ...state.loadingState, isSyncing },
            isSyncing,
          }));
        },
        setScope: (scope) => {
          set({ scope });
        },
        setColumnVisibility: (columnVisibility) => {
          set((state) => ({
            columnVisibility: {
              ...state.columnVisibility,
              ...columnVisibility,
            },
          }));
        },
        setDateRangeValue: (dateRangeValue) => {
          const fromStr = dateRangeValue?.split(",")[0] || undefined;
          const toStr = dateRangeValue?.split(",")[1] || undefined;
          const dateRange = {
            from: fromStr ? parseISO(fromStr) : undefined,
            to: toStr ? parseISO(toStr) : undefined,
          };
          updateURLParams(dateRange);
          set({
            dateRangeValue,
            dateRange,
          });
        },
        setDateRange: (dateRange) => {
          updateURLParams(dateRange);
          set({ dateRange, dateRangeValue: "" });
        },
        setCompanyCurrency: (companyCurrency) => set({ companyCurrency }),
        fetch: async (session) => {
          const { user: authUser } = session || {};

          // TODO: Move user state handling to SWR
          // TODO: Make sure cached user data (e.g. role) can not be changed
          const authRole = getUserRoleFromSession(session);
          const publicUser = authUser ? await getUser(authUser.id) : null;

          // This attaches the auth role to the user and requires another login if role changes
          // Remove to allow instant role changes
          const currentUser = publicUser
            ? {
                ...publicUser,
                role: authRole || publicUser.role,
              }
            : null;

          set({
            currentUser,
            companyCurrency: publicUser?.company?.currency ?? null,
          });
          return currentUser;
        },
        isLoaded: () => {
          const { currentUser } = get();
          return !!currentUser;
        },
        reset: () => set(initialState),
      }),
      {
        name: "revelate-app-storage",
        // Override stored state to ensure loading states are reset on initialization
        partialize: (state) => ({
          // Only persist these specific states and exclude loading states
          scope: state.scope,
          columnVisibility: state.columnVisibility,
          dateRangeValue: state.dateRangeValue,
          dateRange: state.dateRange,
          currentUser: state.currentUser,
          companyCurrency: state.companyCurrency,
          // Explicitly exclude loading states to prevent them from being persisted
          // loadingState: undefined,
          // isCalculating: undefined,
          // isSyncing: undefined,
        }),
        //   return undefined;
        // },
      }
    )
  )
);
